import { isAxiosError } from 'axios';

// Reusable error normalizer
export function getErrorMessage(err: unknown): string {
  if (isAxiosError(err)) {
    const { response, message } = err;

    // Log full error in development for debugging
    if (process.env.NODE_ENV === 'development') {
      console.error('API Error:', {
        message: err.message,
        status: response?.status,
        data: response?.data,
        config: {
          method: err.config?.method,
          url: err.config?.url,
        },
      });
    }

    // No response => network, CORS, timeout, or request aborted
    if (!response) {
      return 'Network error. Please check your connection and try again.';
    }

    const { status, data } = response as { status: number; data?: any };

    // Try to extract a meaningful message from server payloads
    const fromPayload =
      (typeof data === 'string' && data) ||
      (Array.isArray(data) && data.join(', ')) ||
      (data?.detail as string) ||
      (data?.message as string);

    // Friendly status-based fallbacks
    let byStatus: string | undefined;

    switch (status) {
      case 400:
        byStatus = 'Invalid request.';
        break;
      case 401:
        byStatus = 'You are not signed in.';
        break;
      case 403:
        byStatus = 'You do not have permission to perform this action.';
        break;
      case 404:
        byStatus = 'Resource not found.';
        break;
      case 409:
        byStatus = 'Conflict: the item may have already been processed.';
        break;
      case 422:
        byStatus = 'Validation failed. Please check your input.';
        break;
      default:
        if (status >= 500) {
          byStatus = 'Server error. Please try again later.';
        }
    }

    return fromPayload || byStatus || message || 'Something went wrong.';
  }

  // Non-Axios errors
  if (err instanceof Error) {
    // Log unexpected errors in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Non-Axios Error:', err);
    }
    return err.message;
  }

  try {
    return JSON.stringify(err);
  } catch {
    return 'Unexpected error.';
  }
}
